import { useEffect } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';

import {
  storeDetailsSchema,
  type StoreData,
  type StoreDetailsFormInput,
  type StoreDetailsFormValues,
} from '~domains/partner-application/entities/application';
import {
  PARTNER_APPLICATION_TOTAL_STEPS,
  STEP_INDEX_STORES,
} from '~domains/partner-application/shared/constants';

import { FooterWithProgressBar } from '~shared/ui/footer-with-progress-bar';
import { Screen, ScreenHeader } from '~shared/ui/screen';

import { Checkbox } from '@breezeventures/ui-kit/checkbox/rhf';
import { PhoneField } from '@breezeventures/ui-kit/phone-field/rhf';
import { SegmentedControl } from '@breezeventures/ui-kit/segmented-control/rhf';
import { TextField } from '@breezeventures/ui-kit/text-field/rhf';
import { Typography } from '@breezeventures/ui-kit/typography';
import { zodResolver } from '@hookform/resolvers/zod';
import { v4 as uuidv4 } from 'uuid';

type StoreDetailsProps = {
  storeDraft?: Partial<StoreData> | null;
  onBack: (storeType: StoreDetailsFormValues['type']) => void;
  onClose: (storeType: StoreDetailsFormValues['type']) => void;
  onSubmit: (data: StoreDetailsFormValues) => Promise<void>;
};

export const StoreDetails = ({
  storeDraft,
  onBack,
  onClose,
  onSubmit,
}: StoreDetailsProps) => {
  const isEditing = !!storeDraft;
  const methods = useForm<
    StoreDetailsFormInput,
    unknown,
    StoreDetailsFormValues
  >({
    resolver: zodResolver(storeDetailsSchema),
    mode: 'all',
    defaultValues: storeDraft?.details ?? {
      uiId: uuidv4(),
      type: 'OFFLINE',
      name: '',
      email: '',
      contactNumber: '',
      avgMonthlySales: '',
      websiteURL: '',
      tinCode: '',
      areaSqm: '',
      numberOfFloors: '',
      hasCCTV: false,
      dayOfRecording: '',
    },
  });

  useEffect(() => {
    if (storeDraft?.details) {
      methods.trigger();
    }
  }, [storeDraft?.details, methods]);

  const handleStoreTypeChange = (type: 'ONLINE' | 'OFFLINE') => {
    // Reset form with new type, preserving common fields
    const commonValues = {
      uiId: methods.getValues('uiId') ?? '',
      name: methods.getValues('name') ?? '',
      email: methods.getValues('email') ?? '',
      contactNumber: methods.getValues('contactNumber') ?? '',
      avgMonthlySales: methods.getValues('avgMonthlySales') ?? '',
      websiteURL: methods.getValues('websiteURL') ?? '',
    };

    if (type === 'ONLINE') {
      methods.reset({
        type: 'ONLINE',
        ...commonValues,
      });
    } else {
      methods.reset({
        type: 'OFFLINE',
        ...commonValues,
        tinCode: '',
        areaSqm: '',
        numberOfFloors: '',
        hasCCTV: false,
        dayOfRecording: '',
      });
    }
  };

  const storeType = methods.watch('type');
  const hasCCTV = methods.watch('hasCCTV');

  return (
    <form
      onSubmit={methods.handleSubmit(onSubmit)}
      className="flex h-full flex-col"
    >
      <Screen
        header={
          <ScreenHeader
            onBack={() => onBack(storeType)}
            onClose={() => onClose(storeType)}
          />
        }
        footer={
          <FooterWithProgressBar
            currentStepIndex={STEP_INDEX_STORES}
            totalSteps={PARTNER_APPLICATION_TOTAL_STEPS}
            isLoading={methods.formState.isSubmitting}
          />
        }
      >
        <FormProvider {...methods}>
          <div className="flex flex-col gap-3 py-6">
            <Typography.Title size="s">
              Add information about your store
            </Typography.Title>

            <Typography.Text size="m" color="secondary">
              Enter your store&apos;s details.
            </Typography.Text>
          </div>

          <div className="flex flex-col gap-3">
            {!isEditing && (
              <Controller
                name="type"
                control={methods.control}
                render={(props) => (
                  <SegmentedControl
                    disabled={isEditing}
                    width="full"
                    size="m"
                    {...props}
                    field={{
                      ...props.field,
                      onChange: (value) => {
                        props.field.onChange(value);
                        handleStoreTypeChange(value as 'ONLINE' | 'OFFLINE');
                      },
                    }}
                  >
                    <SegmentedControl.Item value="OFFLINE">
                      Physical store
                    </SegmentedControl.Item>
                    <SegmentedControl.Item value="ONLINE">
                      Online store
                    </SegmentedControl.Item>
                  </SegmentedControl>
                )}
              />
            )}

            <Controller
              name="name"
              control={methods.control}
              render={(fieldProps) => (
                <TextField
                  {...fieldProps}
                  label="Store name"
                  labelPosition="inner"
                />
              )}
            />

            <Controller
              name="email"
              control={methods.control}
              render={(fieldProps) => (
                <TextField
                  {...fieldProps}
                  label="Store e-mail address"
                  labelPosition="inner"
                  inputMode="email"
                />
              )}
            />

            <Controller
              name="contactNumber"
              control={methods.control}
              render={(fieldProps) => (
                <PhoneField
                  {...fieldProps}
                  label="Store contact number"
                  labelPosition="inner"
                  inputMode="tel"
                />
              )}
            />

            {storeType === 'OFFLINE' && (
              <Controller
                name="tinCode"
                control={methods.control}
                render={(props) => (
                  <TextField
                    {...props}
                    label="Tax Identification number (TIN)"
                    labelPosition="inner"
                    inputMode="numeric"
                  />
                )}
              />
            )}

            <Controller
              name="avgMonthlySales"
              control={methods.control}
              render={(props) => (
                <TextField
                  {...props}
                  label="Avg sales volume per month (PHP)"
                  labelPosition="inner"
                  inputMode="numeric"
                />
              )}
            />

            <Controller
              name="websiteURL"
              control={methods.control}
              render={(fieldProps) => (
                <TextField
                  {...fieldProps}
                  label={
                    storeType === 'ONLINE'
                      ? 'Website or social media page'
                      : 'Website or social media page (if any)'
                  }
                  labelPosition="inner"
                  inputMode="url"
                />
              )}
            />

            {storeType === 'OFFLINE' && (
              <>
                <Controller
                  name="areaSqm"
                  control={methods.control}
                  render={(props) => (
                    <TextField
                      {...props}
                      label="Store size (sq.m.)"
                      labelPosition="inner"
                      inputMode="numeric"
                    />
                  )}
                />

                <Controller
                  name="numberOfFloors"
                  control={methods.control}
                  render={(props) => (
                    <TextField
                      {...props}
                      label="Number of floors"
                      labelPosition="inner"
                      inputMode="numeric"
                    />
                  )}
                />

                <Controller
                  name="hasCCTV"
                  control={methods.control}
                  render={({ field, ...rest }) => (
                    <Checkbox {...rest} field={field}>
                      <Typography.Text size="m">
                        The store has CCTV cameras
                      </Typography.Text>
                    </Checkbox>
                  )}
                />

                {hasCCTV && (
                  <Controller
                    name="dayOfRecording"
                    control={methods.control}
                    render={(props) => (
                      <TextField
                        {...props}
                        label="How many days are recordings saved?"
                        labelPosition="inner"
                        inputMode="numeric"
                      />
                    )}
                  />
                )}
              </>
            )}
          </div>
        </FormProvider>
      </Screen>
    </form>
  );
};
